import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    addOperatorStaffService,
    updateOperatorStaffService,
    deleteOperatorStaffService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

// Fintrack adding, changing or deleting a member's staff from the CRM - the same three
// things the member's owner can do on their own Staff screen in the Platform.

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type StaffModalMode = "add" | "edit" | "delete";

type Props = {
    companyId: number | string;
    mode: StaffModalMode;
    // The row being edited or deleted. Not used when adding.
    staff: any | null;
    isOpen: boolean;
    toggle: () => void;
    onSaved: () => void;
};

const MemberStaffModal = ({ companyId, mode, staff, isOpen, toggle, onSaved }: Props) => {
    const [firstName, setFirstName] = useState("");
    const [surname, setSurname] = useState("");
    const [mobile, setMobile] = useState("");
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setError("");
        setFirstName(mode === "add" ? "" : staff?.firstName || "");
        setSurname(mode === "add" ? "" : staff?.surname || "");
        setMobile(mode === "add" ? "" : staff?.mobile || "");
        setEmail(mode === "add" ? "" : staff?.email || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const fail = (err: any, fallback: string) => {
        setError(err?.response?.data?.message || fallback);
    };

    const save = async () => {
        if (!firstName.trim() || !surname.trim()) {
            setError("First name and surname are required.");
            return;
        }
        if (!EMAIL_REGEX.test(email.trim())) {
            setError("Enter a valid email address.");
            return;
        }
        const body = {
            firstName: firstName.trim(),
            surname: surname.trim(),
            mobile: mobile.trim() || null,
            email: email.trim(),
        };
        setBusy(true);
        setError("");
        try {
            if (mode === "add") {
                const res: any = await addOperatorStaffService(companyId, body);
                if (res?.data?.inviteEmailSent === false) toast.warn(res?.data?.message || "Added, but the email could not be sent.");
                else toast.success(res?.data?.message || "Staff member added.");
            } else {
                await updateOperatorStaffService(companyId, staff.staffId, body);
                toast.success("Staff member saved.");
            }
            onSaved();
            toggle();
        } catch (err: any) {
            fail(err, "Could not save the staff member.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        setError("");
        try {
            await deleteOperatorStaffService(companyId, staff.staffId);
            toast.success("Staff member deleted.");
            onSaved();
            toggle();
        } catch (err: any) {
            fail(err, "Could not delete the staff member.");
        } finally {
            setBusy(false);
        }
    };

    const title = mode === "add" ? "Add staff" : mode === "edit" ? "Edit staff" : "Delete staff";

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered>
            <ModalHeader toggle={toggle}>{title}</ModalHeader>
            <ModalBody>
                {mode === "delete" ? (
                    <p className="mb-0">
                        Delete{" "}
                        <strong>
                            {staff?.firstName} {staff?.surname}
                        </strong>
                        ? Their login stops straight away. Their reports and EOIs stay with the business.
                    </p>
                ) : (
                    <>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label>First name *</label>
                                <input className="form-control" value={firstName} maxLength={60} onChange={(e) => setFirstName(e.target.value)} />
                            </div>
                            <div className="col-md-6">
                                <label>Surname *</label>
                                <input className="form-control" value={surname} maxLength={60} onChange={(e) => setSurname(e.target.value)} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <label>Mobile</label>
                                <input className="form-control" value={mobile} maxLength={12} onChange={(e) => setMobile(e.target.value)} />
                            </div>
                            <div className="col-md-6">
                                <label>Email *</label>
                                <input type="email" className="form-control" value={email} maxLength={40} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                        </div>
                        {mode === "add" && (
                            <small className="text-muted d-block mt-3">
                                There is no password field - they receive an email to set their own password.
                            </small>
                        )}
                    </>
                )}
                {error && <div className="alert alert-warning mt-3 mb-0">{error}</div>}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={toggle} disabled={busy}>
                    Cancel
                </Button>
                {mode === "delete" ? (
                    <Button color="danger" onClick={remove} disabled={busy}>
                        {busy ? "Deleting..." : "Delete"}
                    </Button>
                ) : (
                    <Button color="primary" onClick={save} disabled={busy}>
                        {busy ? (
                            <>
                                <Spinner size="sm" /> Saving...
                            </>
                        ) : mode === "add" ? (
                            "Add staff"
                        ) : (
                            "Save"
                        )}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default MemberStaffModal;
